// SaveStatusIndicator.js - Visual feedback for auto-save status
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Tooltip, Loader } from '@mantine/core';
import { IconCheck, IconX, IconClock, IconWifi, IconWifiOff } from '@tabler/icons-react';
import useAutoSaveStore from '../../stores/autoSaveStore';

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.status) {
      case 'saving':
        return `
          background: #e3f2fd;
          color: #1976d2;
          border: 1px solid #bbdefb;
        `;
      case 'saved':
        return `
          background: #e8f5e8;
          color: #2e7d32;
          border: 1px solid #c8e6c9;
        `;
      case 'error':
        return `
          background: #ffebee;
          color: #c62828;
          border: 1px solid #ffcdd2;
        `;
      case 'pending':
        return `
          background: #fff3e0;
          color: #f57c00;
          border: 1px solid #ffcc02;
        `;
      case 'offline':
        return `
          background: #f3e5f5;
          color: #7b1fa2;
          border: 1px solid #e1bee7;
        `;
      default:
        return `
          background: #f5f5f5;
          color: #666;
          border: 1px solid #e0e0e0;
        `;
    }
  }}
`;

const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
`;

const StatusText = styled.span`
  white-space: nowrap;
`;

const LastSavedText = styled.span`
  font-size: 11px;
  opacity: 0.8;
  margin-left: 4px;
`;

const SaveStatusIndicator = ({ subcategoryId, showLastSaved = true, compact = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  const {
    getSaveStatus,
    getLastSaveTime,
    getSaveError,
    clearSaveError,
    hasUnsavedChanges,
    isAutoSaveEnabled,
    hasPendingSaves,
    getPendingSaveCount
  } = useAutoSaveStore();

  const saveStatus = getSaveStatus(subcategoryId);
  const lastSaveTime = getLastSaveTime(subcategoryId);
  const saveError = getSaveError(subcategoryId);
  const hasUnsaved = hasUnsavedChanges(subcategoryId);
  const hasPending = hasPendingSaves();
  const pendingCount = getPendingSaveCount();

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Show indicator when there are changes, errors, or offline status
  useEffect(() => {
    const shouldShow = hasUnsaved || saveStatus.status === 'error' || saveStatus.status === 'saving' || !isOnline;
    setIsVisible(shouldShow);
    
    // Auto-hide after successful save (but not if offline)
    if (saveStatus.status === 'saved' && !hasUnsaved && isOnline) {
      const timer = setTimeout(() => setIsVisible(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [saveStatus.status, hasUnsaved, isOnline]);

  // Format last saved time
  const formatLastSaved = (timestamp) => {
    if (!timestamp) return '';
    
    const now = Date.now();
    const diff = now - timestamp;
    
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  // Get status icon
  const getStatusIcon = () => {
    if (!isOnline) {
      return <IconWifiOff size={14} />;
    }
    
    if (!isAutoSaveEnabled) {
      return <IconWifiOff size={14} />;
    }

    switch (saveStatus.status) {
      case 'saving':
      case 'pending':
        return <Loader size={14} />;
      case 'saved':
        return <IconCheck size={14} />;
      case 'error':
        return <IconX size={14} />;
      default:
        return <IconClock size={14} />;
    }
  };

  // Get status text
  const getStatusText = () => {
    if (!isOnline) {
      return compact ? 'Offline' : 'Working offline';
    }
    
    if (!isAutoSaveEnabled) {
      return 'Auto-save disabled';
    }

    switch (saveStatus.status) {
      case 'saving':
      case 'pending':
        return pendingCount > 1 ? `Saving (${pendingCount})...` : 'Saving...';
      case 'saved':
        return compact ? 'Saved' : 'All changes saved';
      case 'error':
        return 'Save failed';
      default:
        return hasUnsaved ? 'Unsaved changes' : 'Saved';
    }
  };

  // Get tooltip content
  const getTooltipContent = () => {
    if (!isOnline) {
      return `Working offline. ${pendingCount} changes queued for when you're back online.`;
    }
    
    if (!isAutoSaveEnabled) {
      return 'Auto-save is disabled. Changes will not be saved automatically.';
    }

    if (saveError) {
      return `Save failed: ${saveError}. Click to retry.`;
    }

    if (hasUnsaved) {
      return 'You have unsaved changes. They will be saved automatically.';
    }

    if (lastSaveTime) {
      return `Last saved: ${formatLastSaved(lastSaveTime)}`;
    }

    return 'All changes are saved.';
  };

  // Handle click (for error retry)
  const handleClick = () => {
    if (saveStatus.status === 'error') {
      clearSaveError(subcategoryId);
    }
  };

  if (!isVisible && !compact) {
    return null;
  }

  const statusElement = (
    <StatusContainer 
      status={!isOnline ? 'offline' : saveStatus.status}
      onClick={saveStatus.status === 'error' ? handleClick : undefined}
      style={{ 
        cursor: saveStatus.status === 'error' ? 'pointer' : 'default',
        opacity: isVisible ? 1 : 0.6
      }}
    >
      <StatusIcon>
        {getStatusIcon()}
      </StatusIcon>
      
      {!compact && (
        <>
          <StatusText>{getStatusText()}</StatusText>
          {showLastSaved && lastSaveTime && saveStatus.status === 'saved' && (
            <LastSavedText>({formatLastSaved(lastSaveTime)})</LastSavedText>
          )}
        </>
      )}
    </StatusContainer>
  );

  if (compact) {
    return statusElement;
  }

  return (
    <Tooltip
      label={getTooltipContent()}
      position="bottom"
      withArrow
      opened={showTooltip}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {statusElement}
    </Tooltip>
  );
};

// Compact version for inline use
export const CompactSaveStatus = ({ subcategoryId }) => (
  <SaveStatusIndicator subcategoryId={subcategoryId} compact={true} />
);

// Full version with tooltip
export const FullSaveStatus = ({ subcategoryId, showLastSaved = true }) => (
  <SaveStatusIndicator 
    subcategoryId={subcategoryId} 
    showLastSaved={showLastSaved} 
    compact={false} 
  />
);

export default SaveStatusIndicator;
